/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, NgZone} from '@angular/core';
import * as _ from 'lodash';
import template from './political-campaign-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {PoliticalEntityResource} from '../../components/api/resources/resource/political-entity.resource';
import {ElectionResource} from '../../components/api/resources/resource/election.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';

const POLITICAL_ENTITY_QUERY_PAGE_SIZE = 2000;

function isPresent(value: any): boolean {
  return value !== null && value !== undefined;
}

function unwrapDomainRef(value: any): any {
  if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'id')) {
    return value.id;
  }
  return value;
}

function stashOwningEntityForCrumbs(m: any): void {
  const raw = isPresent(m.politicalEntity) ? m.politicalEntity : m.politicalEntityId;
  if (raw && typeof raw === 'object' && raw.id) {
    const parentRaw = isPresent(raw.parent) ? raw.parent : raw.parentId;
    let parentStash: any = null;
    if (parentRaw && typeof parentRaw === 'object' && parentRaw.id) {
      parentStash = {id: parentRaw.id, name: parentRaw.name};
    } else if (isPresent(parentRaw) && parentRaw !== '') {
      parentStash = {id: unwrapDomainRef(parentRaw), name: ''};
    }
    m._politicalCampaignCrumbOwning = {
      id: raw.id,
      name: raw.name,
      type: raw.type,
      parent: parentStash
    };
  }
}

function normaliseCampaignRefs(m: any): void {
  stashOwningEntityForCrumbs(m);
  m.politicalEntity = unwrapDomainRef(isPresent(m.politicalEntity) ? m.politicalEntity : m.politicalEntityId);
  m.election = unwrapDomainRef(isPresent(m.election) ? m.election : m.electionId);
  m.payerPoliticalEntity = unwrapDomainRef(isPresent(m.payerPoliticalEntity) ? m.payerPoliticalEntity : m.payerPoliticalEntityId);
  m.ownerTeam = unwrapDomainRef(isPresent(m.ownerTeam) ? m.ownerTeam : m.ownerTeamId);
  delete m.politicalEntityId;
  delete m.electionId;
  delete m.payerPoliticalEntityId;
  delete m.ownerTeamId;
  if (m.topics && m.topics.length) {
    _.forEach(m.topics, (topic: any) => {
      topic.payerPoliticalEntity = unwrapDomainRef(
        isPresent(topic.payerPoliticalEntity) ? topic.payerPoliticalEntity : topic.payerPoliticalEntityId
      );
      delete topic.payerPoliticalEntityId;
    });
  }
}

@Component({
  standalone: false,
  selector: 'adn-political-campaign-edit-page',
  template
})
export class PoliticalCampaignEditPageComponent extends AdminResourceEditPageBase {
  readOnly = false;
  availableCurrencies: string[] = [];
  availableEntities: any[] = [];
  owningEntities: any[] = [];
  payerEntities: any[] = [];
  availableElections: any[] = [];
  availableTeams: any[] = [];

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(PoliticalEntityResource) private readonly PoliticalEntity: any,
    @Inject(ElectionResource) private readonly Election: any,
    @Inject(TeamResource) private readonly Team: any,
    ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.readOnly = !this.LocalUserPermissions.canEditPoliticalResources();
    this.availableCurrencies = LocalNetworkProfile.get('availableCurrencies') || [];
  }

  protected afterInitEditingModel(): void {
    this.editingModel.topics = this.editingModel.topics || [];
    normaliseCampaignRefs(this.editingModel);
    this.loadEntities();
    this.loadElections();
    this.loadTeams();
    if (!this.editingModel.topics.length) {
      this.addTopic();
    }
  }

  protected afterInitAfterSave(): void {
    normaliseCampaignRefs(this.editingModel);
    this.loadEntities();
    this.loadElections();
    this.loadTeams();
  }

  addTopic(): void {
    this.editingModel.topics = this.editingModel.topics || [];
    this.editingModel.topics.push({
      id: '',
      name: '',
      amount: {},
      payerPoliticalEntity: ''
    });
    this.markExternalFormDirty();
  }

  removeTopic(index: number): void {
    this.editingModel.topics.splice(index, 1);
    this.markExternalFormDirty();
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackById(_: number, item: any): string {
    return item.id;
  }

  private loadEntities(): void {
    this.PoliticalEntity.query({pageSize: POLITICAL_ENTITY_QUERY_PAGE_SIZE}).$promise.then((page: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.availableEntities = page.results || [];
        this.owningEntities = this.availableEntities.filter((entity: any) => {
          return entity.type === 'CONTROLLER' || entity.type === 'SPONSOR';
        });
        this.payerEntities = this.availableEntities.filter((entity: any) => {
          return entity.type === 'PAYER';
        });
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.availableEntities = [];
        this.owningEntities = [];
        this.payerEntities = [];
      });
    });
  }

  private loadElections(): void {
    this.Election.query({pageSize: 200}).$promise.then((page: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.availableElections = page.results || [];
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.availableElections = [];
      });
    });
  }

  private loadTeams(): void {
    this.Team.query({minimal: true, pageSize: 2000}).$promise.then((page: any) => {
      // [] → results flips *ngFor on the owner-team <select> mid-CD (NG0100).
      deferMarkForCheck(this.cdr, () => {
        this.availableTeams = (page.results || []).filter((team: any) => {
          return team.type === 'CONTROLLER' || team.type === 'SPONSOR';
        });
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.availableTeams = [];
      });
    });
  }
}
